import { fetchJSON as fetchAPI } from './client';

export type PostSalesDashboard = {
  tenant_id: string;
  open_returns: number;
  pending_refunds: number;
  active_safe_t_claims: number;
  open_cases: number;
  unread_messages: number;
  return_rate_pct: number;
  refund_rate_pct: number;
  claim_success_rate_pct: number;
  avg_response_sla_hrs: number;
  recovery_amount: number;
  active_recommendations: number;
  quality_alerts: number;
  generated_at?: string;
};

export type ReturnRecord = {
  id: string;
  return_id: string;
  amazon_order_id?: string;
  sku?: string;
  asin?: string;
  reason?: string;
  reason_detail?: string;
  status: string;
  quantity: number;
  refund_amount?: number;
  health_score: number;
  abuse_risk?: number;
  quality_risk?: number;
  tracking_number?: string;
  requested_at?: string;
  updated_at?: string;
};

export type RefundRecord = {
  id: string;
  refund_id: string;
  return_id?: string;
  amazon_order_id?: string;
  status: string;
  amount: number;
  currency?: string;
  is_partial: boolean;
  reason?: string;
  processed_at?: string;
  created_at?: string;
};

export type SafeTClaimRecord = {
  id: string;
  claim_id: string;
  amazon_order_id?: string;
  return_id?: string;
  claim_type?: string;
  status: string;
  claim_amount: number;
  recovered_amount?: number;
  evidence_count: number;
  description?: string;
  submitted_at?: string;
  decision_at?: string;
  created_at?: string;
};

export type BuyerMessageRecord = {
  id: string;
  message_id: string;
  amazon_order_id?: string;
  subject?: string;
  body?: string;
  direction?: string;
  sentiment?: string;
  priority?: string;
  sla_deadline?: string;
  is_unread: boolean;
  has_draft: boolean;
  received_at?: string;
};

export type CustomerCaseRecord = {
  id: string;
  case_id: string;
  amazon_order_id?: string;
  title?: string;
  status: string;
  priority?: string;
  assignee?: string;
  category?: string;
  sla_status?: string;
  created_at?: string;
  updated_at?: string;
};

export type ReimbursementRecord = {
  id: string;
  claim_id: string;
  type?: string;
  sku?: string;
  fnsku?: string;
  quantity: number;
  claim_amount: number;
  recovered_amount?: number;
  status?: string;
  fulfillment_center?: string;
  created_at?: string;
};

export type QualityEventRecord = {
  id: string;
  sku?: string;
  event_type?: string;
  description?: string;
  occurrences: number;
  severity?: string;
  trend?: string;
};

export type PostSalesRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  sku?: string;
  amazon_order_id?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

export type SyncJobRecord = {
  id: string;
  mode: string;
  status: string;
  returns_synced: number;
  messages_synced: number;
  failed: number;
  started_at?: string;
  completed_at?: string;
};

const API_BASE = import.meta.env.VITE_POSTSALESOPS_API ?? '/api/v1/postsalesops';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const postsalesopsApi = {
  dashboard: () => fetchJSON<PostSalesDashboard>('/dashboard'),
  listReturns: () => fetchJSON<ReturnRecord[]>('/returns'),
  listRefunds: () => fetchJSON<RefundRecord[]>('/refunds'),
  listSafeTClaims: () => fetchJSON<SafeTClaimRecord[]>('/safe-t'),
  listMessages: () => fetchJSON<BuyerMessageRecord[]>('/messages'),
  listCases: () => fetchJSON<CustomerCaseRecord[]>('/cases'),
  listReimbursements: () => fetchJSON<ReimbursementRecord[]>('/reimbursements'),
  listQuality: () => fetchJSON<QualityEventRecord[]>('/quality'),
  listRecommendations: () => fetchJSON<PostSalesRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<PostSalesRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listSyncJobs: () => fetchJSON<SyncJobRecord[]>('/sync-jobs'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
